import {useFormikContext} from 'formik'
import _ from 'lodash'
import {FC} from 'react'

import {Badge, BadgeColor} from '@/components/common/badges/Badge'
import {Text, TextVariant} from '@/components/common/content/Text'
import {FormMessage} from '@/components/common/form/FormMessage'
import {FormSummaryCountryDetail} from '@/components/common/form/summary/common/FormSummaryCountryDetail'
import {FormSummaryTrademarkPreview} from '@/components/common/form/summary/common/FormSummaryTrademarkPreview'
import {FormSummarySection} from '@/components/common/form/summary/common/layout/FormSummarySection'
import {FormSummary} from '@/components/common/form/summary/FormSummary'
import {FormSummaryPriceWrapper} from '@/components/common/form/summary/price/FormSummaryPriceWrapper'
import {usePreferences} from '@/components/context/Preferences'
import {Spinner} from '@/components/Spinner'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {affiliateConfig} from '@/utils/affiliate/config'
import {
  DiscountCouponEntity,
  TrademarkTypeValue,
  VerificationTrademarkTypeValue,
} from '@/utils/constants'
import {useVerifyDiscountCoupon} from '@/utils/data-fetchers/useVerifyDiscountCoupon'
import {calculatePrice, getMultiCountryDiscount, getNumberOfServices} from '@/utils/pricing'
import {getActiveIpOfficePriceIncreaseInfo} from '@/utils/registration-request/utils'

import {TrademarkVerificationFields} from '../types'

export const Summary: FC = () => {
  const {t} = useTranslation()
  const {affiliate, currency} = usePreferences()
  const {values: valuesMutable} = useFormikContext<TrademarkVerificationFields>()
  const {title, benefit} = useTrademarkVerificationContent().summary
  const {estimatedPriceNote, countriesTitle, freeTitle} = useFormSummaryContent()

  const affiliateCoupon = affiliate ? affiliateConfig[affiliate].discountCoupon : null

  const result = useVerifyDiscountCoupon(affiliateCoupon, DiscountCouponEntity.registrationRequest)

  const values = _.cloneDeep(valuesMutable)
  const price = calculatePrice(
    {...values, numberOfServices: getNumberOfServices({})},
    result?.data ?? null,
    false,
    true,
  )
  const estimatedPrice = price.total.vatBase
  const multiCountryDiscount = getMultiCountryDiscount(price.countries)
  const numOfSelectedCountries = values.jurisdictions.length

  const hasTrademarkPreview =
    (values.type === TrademarkTypeValue.image && values.file) ||
    (values.type === TrademarkTypeValue.text && values.value)

  // we ignore error right now, if it errors we use ostrich technique
  if (result && result?.isLoading) {
    return <Spinner />
  }

  const ipOfficePriceIncreaseInfo = getActiveIpOfficePriceIncreaseInfo().filter(({jurisdiction}) =>
    valuesMutable.jurisdictions.includes(jurisdiction),
  )

  return (
    <>
      <FormSummary
        title={t(title)}
        totalPrice={estimatedPrice}
        currency={currency}
        ipOfficePriceIncreaseInfo={ipOfficePriceIncreaseInfo}
      >
        {hasTrademarkPreview && (
          <FormSummaryTrademarkPreview
            type={values.type as TrademarkTypeValue}
            typeValue={values.typeValue as VerificationTrademarkTypeValue}
            uploadedFile={values.file}
            trademarkTextValue={values.value}
          />
        )}
        {numOfSelectedCountries > 0 && (
          <>
            <FormSummarySection label={t(countriesTitle)}>
              {price.countries.map(({country, legalFees, registrationFees}) => {
                const totalPrice = legalFees.total.vatBase + registrationFees.total.vatBase

                return (
                  <FormSummaryCountryDetail
                    key={country}
                    jurisdiction={country}
                    totalPrice={totalPrice}
                    serviceFee={legalFees.total.vatBase}
                    administrativeFee={registrationFees.total.vatBase}
                    trackedEvent={{
                      name: 'Price breakdown expanded',
                      parameters: {country, page: 'Verification form'},
                    }}
                  />
                )
              })}
            </FormSummarySection>
            <FormSummaryPriceWrapper
              estimatedPrice={estimatedPrice}
              discountCouponPrice={price.couponDiscountsTotal.vatBase}
              hasAppliedDiscountCoupon={price.couponDiscounts.length > 0}
              hasDiscountInput={false}
              multiCountryDiscount={multiCountryDiscount}
              couponId={result?.data?.id}
              priceNote={t(estimatedPriceNote)}
            />
          </>
        )}
        <div className="benefit">
          <FormMessage
            title={t(benefit.title)}
            description={
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                {t(benefit.description)}
              </Text>
            }
            backgroundColor={theme.palette.background.butter}
            borderColor={theme.palette.butter[400]}
            label={<Badge title={t(freeTitle)} color={BadgeColor.green} />}
          />
        </div>
      </FormSummary>

      <style jsx>{`
        .benefit {
          margin-top: ${theme.spacing[4]};
        }
      `}</style>
    </>
  )
}
